import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  Button,
  Icon,
  Spinner,
  Alert,
  Badge,
  SimpleGrid,
  Code,
  Link,
} from '@chakra-ui/react'
import {
  FiArrowLeft,
  FiRefreshCw,
  FiAlertCircle,
  FiExternalLink,
} from 'react-icons/fi'
import { FaFacebook, FaGoogle } from 'react-icons/fa'
import { apiConfig } from '../../config/api'
import { StatusBadge } from '../../components/admin/StatusBadge'

interface CampaignDetail {
  id: string
  practitioner_id: string
  practitioner_name?: string
  practitioner_email?: string
  status: string
  impressions_total: number
  impressions_delivered: number
  selected_platforms?: string[]
  // Meta (Facebook/Instagram)
  meta_status?: string
  meta_error?: string
  meta_campaign_id?: string
  meta_ad_set_id?: string
  // Google Ads
  google_status?: string
  google_error?: string
  google_campaign_id?: string
  google_ad_group_id?: string
  // Common
  pack_id?: string
  practitioner_info?: Record<string, any>
  targeting?: Record<string, any>
  creative?: Record<string, any>
  analytics?: Record<string, any>
  start_date?: string
  end_date?: string
  created_at?: string
  updated_at?: string
}

export const CampaignDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [campaign, setCampaign] = useState<CampaignDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isRetrying, setIsRetrying] = useState(false)

  const fetchCampaign = async () => {
    if (!id) return

    const token = localStorage.getItem('firebase_id_token')
    if (!token) {
      setError('Authentication required')
      setIsLoading(false)
      return
    }

    try {
      const response = await fetch(
        `${apiConfig.baseURL}/api/v1/admin/campaigns/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error('Failed to load campaign')
      }

      const result = await response.json()
      setCampaign(result)
    } catch (err: any) {
      setError(err.message || 'Failed to load campaign')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRetryMeta = async () => {
    if (!id) return

    setIsRetrying(true)
    const token = localStorage.getItem('firebase_id_token')
    if (!token) {
      setError('Authentication required')
      setIsRetrying(false)
      return
    }

    try {
      const response = await fetch(
        `${apiConfig.baseURL}/api/v1/admin/campaigns/${id}/retry-meta`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error('Failed to retry Meta creation')
      }

      // Refresh campaign data
      await fetchCampaign()
    } catch (err: any) {
      setError(err.message || 'Failed to retry Meta creation')
    } finally {
      setIsRetrying(false)
    }
  }

  useEffect(() => {
    fetchCampaign()
  }, [id])

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minH="400px">
        <VStack gap={4}>
          <Spinner size="xl" color="brand.500" />
          <Text color="gray.600">Loading campaign...</Text>
        </VStack>
      </Box>
    )
  }

  if (error || !campaign) {
    return (
      <Container maxW="7xl">
        <Alert.Root status="error">
          <Alert.Indicator />
          <Alert.Content>
            {error || 'Failed to load campaign'}
          </Alert.Content>
        </Alert.Root>
      </Container>
    )
  }

  return (
    <Container maxW="7xl">
      <VStack gap={6} align="stretch">
        {/* Header */}
        <HStack>
          <Button
            leftIcon={<Icon as={FiArrowLeft} />}
            onClick={() => navigate('/admin/campaigns')}
            variant="ghost"
          >
            Back to Campaigns
          </Button>
        </HStack>

        <HStack justify="space-between" align="end">
          <VStack align="start" gap={2}>
            <Heading size="2xl" color="gray.900">
              Campaign Details
            </Heading>
            <Text fontSize="sm" color="gray.500" fontFamily="mono">
              {campaign.id}
            </Text>
          </VStack>
          <Button
            leftIcon={<Icon as={FiRefreshCw} />}
            onClick={fetchCampaign}
            variant="outline"
          >
            Refresh
          </Button>
        </HStack>

        {/* Meta Error Alert */}
        {campaign.meta_error && (
          <Alert.Root status="error">
            <Alert.Indicator />
            <Alert.Content>
              <VStack align="start" gap={2} flex={1}>
                <HStack>
                  <Text fontWeight="bold">Meta Campaign Creation Failed</Text>
                  <StatusBadge status={campaign.meta_status || 'failed'} />
                </HStack>
                <Text fontSize="sm">{campaign.meta_error}</Text>
                <HStack>
                  <Button
                    size="sm"
                    colorPalette="red"
                    onClick={handleRetryMeta}
                    isLoading={isRetrying}
                    leftIcon={<Icon as={FiRefreshCw} />}
                  >
                    Retry Meta Creation
                  </Button>
                </HStack>
              </VStack>
            </Alert.Content>
          </Alert.Root>
        )}

        {/* Key Info */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
          <Card.Root>
            <Card.Body p={5}>
              <VStack align="start" gap={2}>
                <Text fontSize="sm" color="gray.500">
                  Status
                </Text>
                <StatusBadge status={campaign.status} />
              </VStack>
            </Card.Body>
          </Card.Root>

          <Card.Root>
            <Card.Body p={5}>
              <VStack align="start" gap={2}>
                <Text fontSize="sm" color="gray.500">
                  Impressions
                </Text>
                <Text fontWeight="bold" fontSize="lg">
                  {campaign.impressions_delivered.toLocaleString()} /{' '}
                  {campaign.impressions_total.toLocaleString()}
                </Text>
              </VStack>
            </Card.Body>
          </Card.Root>

          <Card.Root>
            <Card.Body p={5}>
              <VStack align="start" gap={2}>
                <Text fontSize="sm" color="gray.500">
                  Pack ID
                </Text>
                <Text fontWeight="bold">{campaign.pack_id || '-'}</Text>
              </VStack>
            </Card.Body>
          </Card.Root>
        </SimpleGrid>

        {/* Practitioner Info */}
        <Card.Root>
          <Card.Body p={6}>
            <VStack align="stretch" gap={4}>
              <Heading size="md" color="gray.900">
                Practitioner
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                <VStack align="start" gap={1}>
                  <Text fontSize="sm" color="gray.500">
                    Name
                  </Text>
                  <Text fontWeight="medium">
                    {campaign.practitioner_name || 'Unknown'}
                  </Text>
                </VStack>
                <VStack align="start" gap={1}>
                  <Text fontSize="sm" color="gray.500">
                    Email
                  </Text>
                  <Text>{campaign.practitioner_email || '-'}</Text>
                </VStack>
                <VStack align="start" gap={1}>
                  <Text fontSize="sm" color="gray.500">
                    Practitioner ID
                  </Text>
                  <Link
                    color="blue.500"
                    onClick={() =>
                      navigate(`/admin/practitioners/${campaign.practitioner_id}`)
                    }
                    cursor="pointer"
                  >
                    {campaign.practitioner_id}
                  </Link>
                </VStack>
              </SimpleGrid>
            </VStack>
          </Card.Body>
        </Card.Root>

        {/* Platform Integrations */}
        <Card.Root>
          <Card.Body p={6}>
            <VStack align="stretch" gap={6}>
              <Heading size="md" color="gray.900">
                Ad Platform Integrations
              </Heading>
              
              {/* Selected Platforms */}
              {campaign.selected_platforms && campaign.selected_platforms.length > 0 && (
                <HStack gap={2}>
                  <Text fontSize="sm" color="gray.500">Selected Platforms:</Text>
                  {campaign.selected_platforms.map(platform => (
                    <Badge key={platform} colorPalette={platform === 'meta' ? 'blue' : 'red'} variant="subtle">
                      {platform === 'meta' ? 'Meta (Facebook/Instagram)' : 'Google Ads'}
                    </Badge>
                  ))}
                </HStack>
              )}

              {/* Meta Integration */}
              {(campaign.selected_platforms?.includes('meta') || campaign.meta_status || campaign.meta_campaign_id) && (
                <Box p={4} borderWidth="1px" borderRadius="md" borderColor="blue.200" bg="blue.50">
                  <HStack gap={2} mb={3}>
                    <Icon as={FaFacebook} color="blue.600" boxSize={5} />
                    <Heading size="sm" color="gray.800">Meta (Facebook & Instagram)</Heading>
                  </HStack>
                  <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
                    <VStack align="start" gap={1}>
                      <Text fontSize="sm" color="gray.500">Status</Text>
                      {campaign.meta_status ? (
                        <StatusBadge status={campaign.meta_status} />
                      ) : (
                        <Text color="gray.400">Not created</Text>
                      )}
                    </VStack>
                    <VStack align="start" gap={1}>
                      <Text fontSize="sm" color="gray.500">Campaign ID</Text>
                      {campaign.meta_campaign_id ? (
                        <HStack>
                          <Text fontFamily="mono" fontSize="sm">{campaign.meta_campaign_id}</Text>
                          <Link href={`https://business.facebook.com/adsmanager/manage/campaigns?act=${campaign.meta_campaign_id}`} isExternal>
                            <Icon as={FiExternalLink} />
                          </Link>
                        </HStack>
                      ) : (
                        <Text color="gray.400">-</Text>
                      )}
                    </VStack>
                    <VStack align="start" gap={1}>
                      <Text fontSize="sm" color="gray.500">Ad Set ID</Text>
                      <Text fontFamily="mono" fontSize="sm">{campaign.meta_ad_set_id || '-'}</Text>
                    </VStack>
                  </SimpleGrid>
                  {campaign.meta_error && (
                    <Box mt={3} p={2} bg="red.100" borderRadius="md">
                      <HStack gap={1}>
                        <Icon as={FiAlertCircle} color="red.500" />
                        <Text fontSize="sm" color="red.700">{campaign.meta_error}</Text>
                      </HStack>
                    </Box>
                  )}
                </Box>
              )}

              {/* Google Ads Integration */}
              {(campaign.selected_platforms?.includes('google') || campaign.google_status || campaign.google_campaign_id) && (
                <Box p={4} borderWidth="1px" borderRadius="md" borderColor="red.200" bg="red.50">
                  <HStack gap={2} mb={3}>
                    <Icon as={FaGoogle} color="red.500" boxSize={5} />
                    <Heading size="sm" color="gray.800">Google Ads</Heading>
                  </HStack>
                  <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
                    <VStack align="start" gap={1}>
                      <Text fontSize="sm" color="gray.500">Status</Text>
                      {campaign.google_status ? (
                        <StatusBadge status={campaign.google_status} />
                      ) : (
                        <Text color="gray.400">Not created</Text>
                      )}
                    </VStack>
                    <VStack align="start" gap={1}>
                      <Text fontSize="sm" color="gray.500">Campaign ID</Text>
                      {campaign.google_campaign_id ? (
                        <HStack>
                          <Text fontFamily="mono" fontSize="sm">{campaign.google_campaign_id}</Text>
                          <Link href={`https://ads.google.com/aw/campaigns?campaignId=${campaign.google_campaign_id}`} isExternal>
                            <Icon as={FiExternalLink} />
                          </Link>
                        </HStack>
                      ) : (
                        <Text color="gray.400">-</Text>
                      )}
                    </VStack>
                    <VStack align="start" gap={1}>
                      <Text fontSize="sm" color="gray.500">Ad Group ID</Text>
                      <Text fontFamily="mono" fontSize="sm">{campaign.google_ad_group_id || '-'}</Text>
                    </VStack>
                  </SimpleGrid>
                  {campaign.google_error && (
                    <Box mt={3} p={2} bg="red.100" borderRadius="md">
                      <HStack gap={1}>
                        <Icon as={FiAlertCircle} color="red.500" />
                        <Text fontSize="sm" color="red.700">{campaign.google_error}</Text>
                      </HStack>
                    </Box>
                  )}
                </Box>
              )}
            </VStack>
          </Card.Body>
        </Card.Root>

        {/* Creative */}
        {campaign.creative && (
          <Card.Root>
            <Card.Body p={6}>
              <VStack align="stretch" gap={4}>
                <Heading size="md" color="gray.900">
                  Creative
                </Heading>
                <VStack align="start" gap={2}>
                  {campaign.creative.headline && (
                    <VStack align="start" gap={1}>
                      <Text fontSize="sm" color="gray.500">
                        Headline
                      </Text>
                      <Text>{campaign.creative.headline}</Text>
                    </VStack>
                  )}
                  {campaign.creative.body && (
                    <VStack align="start" gap={1}>
                      <Text fontSize="sm" color="gray.500">
                        Body
                      </Text>
                      <Text>{campaign.creative.body}</Text>
                    </VStack>
                  )}
                  {campaign.creative.image_url && (
                    <VStack align="start" gap={1}>
                      <Text fontSize="sm" color="gray.500">
                        Image
                      </Text>
                      <Box>
                        <img
                          src={campaign.creative.image_url}
                          alt="Ad creative"
                          style={{ maxWidth: '300px', borderRadius: '8px' }}
                        />
                      </Box>
                    </VStack>
                  )}
                </VStack>
              </VStack>
            </Card.Body>
          </Card.Root>
        )}

        {/* Targeting */}
        {campaign.targeting && (
          <Card.Root>
            <Card.Body p={6}>
              <VStack align="stretch" gap={4}>
                <Heading size="md" color="gray.900">
                  Targeting
                </Heading>
                <Box>
                  <Code p={4} display="block" whiteSpace="pre-wrap">
                    {JSON.stringify(campaign.targeting, null, 2)}
                  </Code>
                </Box>
              </VStack>
            </Card.Body>
          </Card.Root>
        )}

        {/* Analytics */}
        {campaign.analytics && (
          <Card.Root>
            <Card.Body p={6}>
              <VStack align="stretch" gap={4}>
                <Heading size="md" color="gray.900">
                  Analytics
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                  <VStack align="start" gap={1}>
                    <Text fontSize="sm" color="gray.500">
                      Clicks
                    </Text>
                    <Text fontWeight="bold">
                      {campaign.analytics.clicks || 0}
                    </Text>
                  </VStack>
                  <VStack align="start" gap={1}>
                    <Text fontSize="sm" color="gray.500">
                      CTR
                    </Text>
                    <Text fontWeight="bold">
                      {campaign.analytics.ctr
                        ? `${(campaign.analytics.ctr * 100).toFixed(2)}%`
                        : '0%'}
                    </Text>
                  </VStack>
                </SimpleGrid>
              </VStack>
            </Card.Body>
          </Card.Root>
        )}
      </VStack>
    </Container>
  )
}

